import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation } from "react-query";
import * as yup from "yup";
import { FormSelectAPI } from "../../components/ui/FormReactSelect";
import { Input } from "../../components/inputs/Input";
import { CSpinner } from "@coreui/react";
import { useCookie } from "../../hooks/useCookie";
import { useToast } from "../../hooks/useToast";
import { general } from "../../locales/general";
import apiService from "../../service/apiService";
import { AxiosError } from "axios";
import { isNotEmpty } from "../../helpers/general";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import "../../styles/Generalinfoform.css";
import {
  DEFAULT_PORTAL_PATHS,
  PORTAL_PATH_OPTIONS,
} from "../../helpers/constants";

interface Props {
  data?: Driver | User;
  role?: "user" | "driver";
  invalidateQuery?: () => void;
}

const validations = yup.object().shape({
  fullName: yup.string().required("Ce champ est obligatoire"),
  email: yup.string().required("Ce champ est obligatoire"),
  phone: yup.string().required("Ce champ est obligatoire"),
  birthday: yup.mixed().optional(),
  cityId: yup.mixed().optional(),
  cinNumber: yup.string().optional(),
  isVerifiedPhone: yup.boolean().default(false),
  allowedPortalPaths: yup.array().of(yup.string().required()).optional(),
});

type FormInput = {
  fullName: string;
  email: string;
  phone: string;
  cinNumber?: string;
  birthday?: any;
  cityId?: any;
  isVerifiedPhone: boolean;
  allowedPortalPaths?: string[];
};

function Toggle({
  id,
  checked,
  onChange,
}: {
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="switch mb-0">
      <input
        type="checkbox"
        id={id}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="slider round"></span>
    </label>
  );
}

export default function GeneralInfoForm(props: Props) {
  const { data, role } = props;
  const { token } = useCookie("vToken");
  const { toastSuccess, toastError } = useToast();
  const isPartner = data?.role?.label === "Partner";
  const isGuest = data?.email === "guest-user@venez.ma";
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormInput>({
    resolver: yupResolver(validations),
  });

  const selectedPaths = watch("allowedPortalPaths") ?? [];
  const customPaths = selectedPaths.filter(
    (p) => !PORTAL_PATH_OPTIONS.some((o) => o.value === p),
  );

  useEffect(() => {
    const defaultValues = {
      fullName: data?.fullName,
      email: data?.email,
      phone: data?.phone,
      cinNumber: data?.cinNumber,
      birthday: data?.birthday,
      cityId: data?.city,
      isVerifiedPhone: data?.isVerifiedPhone ?? false,
      allowedPortalPaths: isPartner
        ? ((data as any)?.allowedPortalPaths ?? DEFAULT_PORTAL_PATHS)
        : undefined,
    };
    if (defaultValues) {
      Object.entries(defaultValues).forEach(([key, value]) => {
        if (value) {
          if (key === "birthday") {
            setValue(
              key as any,
              new Date(value as string).toISOString().split("T")[0],
            );
          } else {
            setValue(key as any, value);
          }
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const updateProfile = useMutation((payload: FormInput) => {
    return apiService.MakePutRequest(
      `users/${data?.id}/driver`,
      payload,
      token,
    );
  });

  const updateProfileHandler = async (data: FormInput) => {
    try {
      await updateProfile.mutateAsync({
        ...data,
        cityId: data?.cityId?.id,
        isVerifiedPhone: data.isVerifiedPhone,
        ...(isPartner && {
          allowedPortalPaths: Array.from(
            new Set([...(data.allowedPortalPaths ?? [])]),
          ),
        }),
      });
      toastSuccess(general.fr.message.profileUpdated);
      props?.invalidateQuery?.();
    } catch (error) {
      let errorMessage = general.fr.message.operationFailed;
      if (error instanceof AxiosError) {
        const apiError = error.response?.data as VenezApiError;
        if (isNotEmpty(apiError?.message)) {
          errorMessage = apiError.message;
        }
      }
      toastError(errorMessage);
    }
  };

  const fieldError = (name: keyof FormInput) =>
    errors?.[name]?.message ? (
      <p className="gi-error">{String(errors[name]?.message)}</p>
    ) : null;

  return (
    <section className={`gi-card my-3 ${isGuest ? "gi-guest" : ""}`}>
      {/* Hero */}
      <div className="gi-hero">
        <img
          src={
            (data?.profilePicture as string) ||
            `https://ui-avatars.com/api/?name=${encodeURIComponent(data?.fullName ?? "")}`
          }
          alt=""
        />
        <div className="min-w-0">
          <h4>{data?.fullName}</h4>
          <p>{data?.email}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit(updateProfileHandler)} className="gi-body">
        {/* Personal info */}
        <p className="gi-section-title">Informations personnelles</p>
        <div className="row">
          <div
            className={`col-md-6 mb-3 ${errors.fullName ? "gi-has-error" : ""}`}
          >
            <label htmlFor="fullName" className="gi-label">
              Nom complet
            </label>
            <Input
              type="text"
              className="custom-input"
              name="fullName"
              placeholder="Nom complet"
              id="fullName"
              control={control}
            />
            {fieldError("fullName")}
          </div>

          <div className="col-md-6 mb-3">
            <label htmlFor="email" className="gi-label">
              Email
            </label>
            <Input
              type="email"
              className="custom-input"
              name="email"
              placeholder="Email"
              id="email"
              control={control}
              disabled
              readOnly
            />
          </div>

          <div
            className={`col-md-6 mb-3 ${errors.phone ? "gi-has-error" : ""}`}
          >
            <label htmlFor="phone" className="gi-label">
              Téléphone
            </label>
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <PhoneInput
                  id="phone"
                  placeholder="Téléphone"
                  value={field.value}
                  defaultCountry="MA"
                  international
                  required
                  className="phone-input"
                  onChange={(value) => field.onChange(value ?? "")}
                />
              )}
            />
            {fieldError("phone")}
          </div>

          <div className="col-md-6 mb-3">
            <label htmlFor="isVerifiedPhone" className="gi-label">
              Téléphone vérifié
            </label>
            <Controller
              name="isVerifiedPhone"
              control={control}
              render={({ field }) => (
                <div className="gi-toggle">
                  <span className="gi-toggle-text">
                    {field.value ? "✅ Vérifié" : "❌ Non vérifié"}
                  </span>
                  <Toggle
                    id="isVerifiedPhone"
                    checked={!!field.value}
                    onChange={field.onChange}
                  />
                </div>
              )}
            />
          </div>
        </div>

        <div className="gi-divider" />

        {/* Details */}
        <p className="gi-section-title">Détails</p>
        <div className="row">
          <div className="col-md-6 mb-3">
            <label htmlFor="birthday" className="gi-label">
              Date de naissance
            </label>
            <Input
              type="date"
              className="custom-input"
              placeholder="Date"
              id="date"
              name="birthday"
              control={control}
            />
          </div>

          <div className="col-md-6 mb-3 gi-select">
            <label htmlFor="cityId" className="gi-label">
              Ville
            </label>
            <Controller
              name="cityId"
              control={control}
              render={({ field }) => (
                <FormSelectAPI
                  id="cityId"
                  isClearable={false}
                  getOptionLabel={(option: City) => option?.label}
                  getOptionValue={(option: City) => option.id}
                  isOptionSelected={(option: City, selectedValue: City[]) =>
                    option?.id === selectedValue?.[0]?.id
                  }
                  url={{ path: "cities" }}
                  {...field}
                />
              )}
            />
            {fieldError("cityId")}
          </div>

          {role === "driver" && (
            <div className="col-md-6 mb-3">
              <label htmlFor="cin" className="gi-label">
                CIN
              </label>
              <Input
                className="custom-input"
                name="cinNumber"
                placeholder="CIN"
                id="cin"
                control={control}
              />
            </div>
          )}
        </div>

        {/* Partner portal */}
        {isPartner && (
          <div className="gi-portal">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <p className="gi-section-title mb-0">
                Pages visibles dans le portail partenaire
              </p>
              <button
                type="button"
                className="btn btn-sm btn-link text-danger p-0"
                onClick={() =>
                  setValue("allowedPortalPaths", DEFAULT_PORTAL_PATHS)
                }
              >
                Réinitialiser
              </button>
            </div>

            <div className="row g-2">
              {PORTAL_PATH_OPTIONS.map((option) => (
                <div className="col-md-6" key={option.value}>
                  <div className="gi-toggle">
                    <label
                      htmlFor={`portal-path-${option.value}`}
                      className="text-sm mb-0"
                    >
                      {option.label}
                    </label>
                    <Toggle
                      id={`portal-path-${option.value}`}
                      checked={selectedPaths.includes(option.value)}
                      onChange={(checked) =>
                        setValue(
                          "allowedPortalPaths",
                          checked
                            ? [...selectedPaths, option.value]
                            : selectedPaths.filter((p) => p !== option.value),
                          { shouldDirty: true },
                        )
                      }
                    />
                  </div>
                </div>
              ))}

              {customPaths.map((path) => (
                <div className="col-md-6" key={path}>
                  <div className="gi-toggle">
                    <span className="text-sm">
                      {path}
                      <span className="badge bg-secondary ms-2">perso</span>
                    </span>
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-danger p-0"
                      onClick={() =>
                        setValue(
                          "allowedPortalPaths",
                          selectedPaths.filter((p) => p !== path),
                          { shouldDirty: true },
                        )
                      }
                    >
                      Retirer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="gi-footer">
          <button
            type="submit"
            disabled={updateProfile.isLoading}
            className="btn btn-danger rounded-pill py-2 px-4 text-white"
          >
            {updateProfile.isLoading ? (
              <CSpinner size="sm" />
            ) : (
              "Mettre à jour les informations"
            )}
          </button>
        </div>
      </form>
    </section>
  );
}
